import { supabase } from '../lib/supabase';

// Helper to check if a string is a valid UUID format
const isValidUUID = (str) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export const ratingService = {
  // Product Ratings

  async getProductRatings(productId) {
    const { data, error } = await supabase?.from('product_ratings')?.select(`
        *,
        users!product_ratings_user_id_fkey (
          first_name,
          last_name,
          email,
          avatar_url
        )
      `)?.eq('product_id', productId)?.order('created_at', { ascending: false });

    if (error) throw error;

    // Fetch vote counts for each rating
    const ratingsWithVotes = await Promise.all(
      (data || [])?.map(async (rating) => {
        const voteCounts = await this.getReviewVoteCounts(rating?.id, 'product');
        
        return {
          id: rating?.id,
          productId: rating?.product_id,
          userId: rating?.user_id,
          rating: rating?.rating,
          review: rating?.review,
          createdAt: rating?.created_at,
          updatedAt: rating?.updated_at,
          helpfulCount: voteCounts?.helpful || 0,
          notHelpfulCount: voteCounts?.notHelpful || 0,
          user: {
            firstName: rating?.users?.first_name,
            lastName: rating?.users?.last_name,
            email: rating?.users?.email,
            avatarUrl: rating?.users?.avatar_url
          }
        };
      })
    );

    return ratingsWithVotes;
  },

  async getUserProductRating(productId, userId) {
    const { data, error } = await supabase?.from('product_ratings')?.select('*')?.eq('product_id', productId)?.eq('user_id', userId)?.maybeSingle();

    if (error) throw error;

    if (!data) return null;

    return {
      id: data?.id,
      productId: data?.product_id,
      userId: data?.user_id,
      rating: data?.rating,
      review: data?.review,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async createProductRating(productId, rating, review = null) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    console.log('Attempting to create product rating:', {
      productId,
      userId: user?.id,
      rating,
      review
    });

    const { data, error } = await supabase?.from('product_ratings')?.insert({
        product_id: productId,
        user_id: user?.id,
        rating,
        review
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      productId: data?.product_id,
      userId: data?.user_id,
      rating: data?.rating,
      review: data?.review,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async updateProductRating(ratingId, rating, review = null) {
    const { data, error } = await supabase?.from('product_ratings')?.update({
        rating,
        review,
        updated_at: new Date()?.toISOString()
      })?.eq('id', ratingId)?.select(); // Removed .single()

    if (error) throw error;

    if (!data || data.length === 0) {
      throw new Error(`Product rating with ID ${ratingId} not found or not authorized to update.`);
    }

    return {
      id: data[0]?.id,
      productId: data[0]?.product_id,
      userId: data[0]?.user_id,
      rating: data[0]?.rating,
      review: data[0]?.review,
      createdAt: data[0]?.created_at,
      updatedAt: data[0]?.updated_at
    };
  },

  async deleteProductRating(ratingId) {
    const { error } = await supabase?.from('product_ratings')?.delete()?.eq('id', ratingId);

    if (error) throw error;
  },

  // Seller Ratings (NEW)
  async getSellerRatings(sellerIdentifier) {
    // First, get the seller_id from seller_accounts table using the identifier (slug or user_id)
    let sellerAccountId;
    if (isValidUUID(sellerIdentifier)) { // If it's a UUID, check both id (PK) and user_id
        const { data: sellerAccount, error: uuidError } = await supabase
            ?.from('seller_accounts')
            ?.select('id')
            ?.or(`id.eq.${sellerIdentifier},user_id.eq.${sellerIdentifier}`)
            ?.maybeSingle();
        if (uuidError) throw uuidError;
        sellerAccountId = sellerAccount?.id;
    } else { // Otherwise, assume it's a business_slug
        const { data: sellerAccount, error: slugError } = await supabase
            ?.from('seller_accounts')
            ?.select('id')
            ?.eq('business_slug', sellerIdentifier)
            ?.maybeSingle();
        if (slugError) throw slugError;
        sellerAccountId = sellerAccount?.id;
    }

    if (!sellerAccountId) throw new Error('Seller not found.');

    const { data, error } = await supabase?.from('seller_ratings')?.select(`
        *,
        users!seller_ratings_user_id_fkey (
          first_name,
          last_name,
          email,
          avatar_url
        )
      `)?.eq('seller_id', sellerAccountId)?.order('created_at', { ascending: false });

    if (error) throw error;

    const ratingsWithVotes = await Promise.all(
      (data || [])?.map(async (rating) => {
        const voteCounts = await this.getReviewVoteCounts(rating?.id, 'seller');
        
        return {
          id: rating?.id,
          sellerId: rating?.seller_id,
          userId: rating?.user_id,
          rating: rating?.rating,
          review: rating?.review,
          createdAt: rating?.created_at,
          updatedAt: rating?.updated_at,
          helpfulCount: voteCounts?.helpful || 0,
          notHelpfulCount: voteCounts?.notHelpful || 0,
          user: {
            firstName: rating?.users?.first_name,
            lastName: rating?.users?.last_name,
            email: rating?.users?.email,
            avatarUrl: rating?.users?.avatar_url
          }
        };
      })
    );

    return ratingsWithVotes;
  },

  async getUserSellerRating(sellerIdentifier, userId) {
    let sellerAccountId;
    if (isValidUUID(sellerIdentifier)) {
        const { data: sellerAccount, error: uuidError } = await supabase
            ?.from('seller_accounts')
            ?.select('id')
            ?.or(`id.eq.${sellerIdentifier},user_id.eq.${sellerIdentifier}`)
            ?.maybeSingle();
        if (uuidError) throw uuidError;
        sellerAccountId = sellerAccount?.id;
    } else {
        const { data: sellerAccount, error: slugError } = await supabase
            ?.from('seller_accounts')
            ?.select('id')
            ?.eq('business_slug', sellerIdentifier)
            ?.maybeSingle();
        if (slugError) throw slugError;
        sellerAccountId = sellerAccount?.id;
    }

    if (!sellerAccountId) return null;

    const { data, error } = await supabase?.from('seller_ratings')?.select('*')?.eq('seller_id', sellerAccountId)?.eq('user_id', userId)?.maybeSingle();

    if (error) throw error;

    if (!data) return null;

    return {
      id: data?.id,
      sellerId: data?.seller_id,
      userId: data?.user_id,
      rating: data?.rating,
      review: data?.review,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async createSellerRating(sellerIdentifier, rating, review = null) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let sellerAccountId;
    if (isValidUUID(sellerIdentifier)) {
        const { data: sellerAccount, error: uuidError } = await supabase
            ?.from('seller_accounts')
            ?.select('id')
            ?.or(`id.eq.${sellerIdentifier},user_id.eq.${sellerIdentifier}`)
            ?.maybeSingle();
        if (uuidError) throw uuidError;
        sellerAccountId = sellerAccount?.id;
    } else {
        const { data: sellerAccount, error: slugError } = await supabase
            ?.from('seller_accounts')
            ?.select('id')
            ?.eq('business_slug', sellerIdentifier)
            ?.maybeSingle();
        if (slugError) throw slugError;
        sellerAccountId = sellerAccount?.id;
    }

    if (!sellerAccountId) throw new Error('Seller not found.');

    const { data, error } = await supabase?.from('seller_ratings')?.insert({
        seller_id: sellerAccountId,
        user_id: user?.id,
        rating,
        review
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      sellerId: data?.seller_id,
      userId: data?.user_id,
      rating: data?.rating,
      review: data?.review,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async updateSellerRating(ratingId, rating, review = null) {
    const { data, error } = await supabase?.from('seller_ratings')?.update({
        rating,
        review,
        updated_at: new Date()?.toISOString()
      })?.eq('id', ratingId)?.select();

    if (error) throw error;

    if (!data || data.length === 0) {
      throw new Error(`Seller rating with ID ${ratingId} not found or not authorized to update.`);
    }

    return {
      id: data[0]?.id,
      sellerId: data[0]?.seller_id,
      userId: data[0]?.user_id,
      rating: data[0]?.rating,
      review: data[0]?.review,
      createdAt: data[0]?.created_at,
      updatedAt: data[0]?.updated_at
    };
  },

  async deleteSellerRating(ratingId) {
    const { error } = await supabase?.from('seller_ratings')?.delete()?.eq('id', ratingId);

    if (error) throw error;
  },

  // Component Ratings

  async getComponentRatings(componentId) {
    const { data, error } = await supabase?.from('component_ratings')?.select(`
        *,
        users!component_ratings_user_id_fkey (
          first_name,
          last_name,
          email,
          avatar_url
        )
      `)
      ?.eq('component_id', componentId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(rating => ({
      id: rating?.id,
      componentId: rating?.component_id,
      userId: rating?.user_id,
      rating: rating?.rating,
      review: rating?.review,
      createdAt: rating?.created_at,
      updatedAt: rating?.updated_at,
      user: {
        firstName: rating?.users?.first_name,
        lastName: rating?.users?.last_name,
        email: rating?.users?.email,
        avatarUrl: rating?.users?.avatar_url
      }
    })) || [];
  },

  async getUserComponentRating(componentId, userId) {
    const { data, error } = await supabase?.from('component_ratings')?.select('*')?.eq('component_id', componentId)?.eq('user_id', userId)?.maybeSingle();

    if (error) throw error;

    if (!data) return null;

    return {
      id: data?.id,
      componentId: data?.component_id,
      userId: data?.user_id,
      rating: data?.rating,
      review: data?.review,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async createComponentRating(componentId, rating, review = null) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('component_ratings')?.insert({
        component_id: componentId,
        user_id: user?.id,
        rating,
        review
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      componentId: data?.component_id,
      userId: data?.user_id,
      rating: data?.rating,
      review: data?.review,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async updateComponentRating(ratingId, rating, review = null) {
    const { data, error } = await supabase?.from('component_ratings')?.update({
        rating,
        review,
        updated_at: new Date()?.toISOString()
      })?.eq('id', ratingId)?.select(); // Removed .single()

    if (error) throw error;

    if (!data || data.length === 0) {
      throw new Error(`Component rating with ID ${ratingId} not found or not authorized to update.`);
    }

    return {
      id: data[0]?.id,
      componentId: data[0]?.component_id,
      userId: data[0]?.user_id,
      rating: data[0]?.rating,
      review: data[0]?.review,
      createdAt: data[0]?.created_at,
      updatedAt: data[0]?.updated_at
    };
  },

  async deleteComponentRating(ratingId) {
    const { error } = await supabase?.from('component_ratings')?.delete()?.eq('id', ratingId);

    if (error) throw error;
  },

  // Review Voting Functions

  async voteOnReview(ratingId, ratingType, voteType) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Check if user has already voted
    const { data: existingVote } = await supabase
      ?.from('review_votes')
      ?.select('*')
      ?.eq('user_id', user?.id)
      ?.eq('rating_id', ratingId)
      ?.eq('rating_type', ratingType)
      ?.maybeSingle();

    if (existingVote) {
      // If same vote type, remove the vote (toggle off)
      if (existingVote?.vote_type === voteType) {
        const { error } = await supabase
          ?.from('review_votes')
          ?.delete()
          ?.eq('id', existingVote?.id);

        if (error) throw error;
        return { removed: true };
      } else {
        // If different vote type, update the vote
        const { data, error } = await supabase
          ?.from('review_votes')
          ?.update({ vote_type: voteType, updated_at: new Date()?.toISOString() })
          ?.eq('id', existingVote?.id)
          ?.select()
          ?.single();

        if (error) throw error;
        return { updated: true, vote: data };
      }
    } else {
      // Create new vote
      const { data, error } = await supabase
        ?.from('review_votes')
        ?.insert({
          user_id: user?.id,
          rating_id: ratingId,
          rating_type: ratingType,
          vote_type: voteType
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { created: true, vote: data };
    }
  },

  async getUserVote(ratingId, ratingType) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      ?.from('review_votes')
      ?.select('vote_type')
      ?.eq('user_id', user?.id)
      ?.eq('rating_id', ratingId)
      ?.eq('rating_type', ratingType)
      ?.maybeSingle();

    if (error) throw error;
    return data?.vote_type || null;
  },

  async getReviewVoteCounts(ratingId, ratingType) {
    const { data, error } = await supabase
      ?.from('review_votes')
      ?.select('vote_type')
      ?.eq('rating_id', ratingId)
      ?.eq('rating_type', ratingType);

    if (error) throw error;

    const helpfulCount = data?.filter(v => v?.vote_type === 'helpful')?.length || 0;
    const notHelpfulCount = data?.filter(v => v?.vote_type === 'not_helpful')?.length || 0;

    return {
      helpful: helpfulCount,
      notHelpful: notHelpfulCount,
      total: helpfulCount + notHelpfulCount
    };
  }
};

export default ratingService;